import {
  EXPORT_ALERT_LABELS,
  EXPORT_ALERT_RULES,
  EXPORT_POLICY_ROLES,
  EXPORT_TYPE_LABELS,
  type ExportAlert,
  type ExportControlPolicy,
  type ExportRecord,
  findExportId,
  MAX_EXPORT_THRESHOLD,
} from '@hoopoe/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { BellOff, FileSearch, Save, Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// Admin → Exports (task 36; PLAN §4 `exports`, §11 export controls): every file that left the app, newest first, and
// "Trace a file": paste the export ID (or the whole watermark line) printed on a file to see who exported it, when
// and from what. Task 38 adds the export controls: the export policy (which roles may export; admins always can),
// the bulk-export thresholds, and the alerts raised when an export crosses one.

export const Route = createFileRoute('/admin/exports')({ component: ExportsScreen });

const TOUR = {
  id: 'admin-exports',
  steps: [
    {
      target: '[data-tour="exports-trace"]',
      title: 'Trace a file',
      body: 'Every exported page carries a watermark with an export ID. Paste the ID, or the whole watermark line, to see who exported the file, when and what it held.',
      learnMore: 'exports-and-watermarks',
    },
    {
      target: '[data-tour="exports-policy"]',
      title: 'Who may export, and what counts as bulk',
      body: 'Choose which roles may export. An export that crosses a threshold is still made, but every admin gets an alert.',
      learnMore: 'export-controls',
    },
    {
      target: '[data-tour="exports-list"]',
      title: 'Every export',
      body: 'Reports, profile sheets and graph images, newest first. The audit log keeps the same events with more detail.',
    },
  ],
};

const when = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const size = (n: number | null) =>
  n === null ? '—' : n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`;

function ExportsScreen() {
  useScreenTour(TOUR);
  const list = useInfiniteQuery({
    queryKey: ['admin', 'exports'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ exports: ExportRecord[]; next: number | null }>(`/admin/exports${pageParam ? `?before=${pageParam}` : ''}`),
    getNextPageParam: last => last.next ?? undefined,
  });
  const rows = list.data?.pages.flatMap(p => p.exports) ?? [];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Exports"
        description="Every file exported from Hoopoe, and who made it. Each page of a file carries its export ID in the watermark."
      />
      <Trace />
      <PolicySection />
      <AlertsSection />
      <section aria-labelledby="exports-recent" className="space-y-3" data-tour="exports-list">
        <h2 id="exports-recent" className="text-base font-semibold">
          Recent exports
        </h2>
        {list.isError ? (
          <ErrorState
            error={list.error}
            title="The exports couldn’t be loaded"
            onRetry={() => list.refetch()}
            learnMore="exports-and-watermarks"
          />
        ) : list.isPending ? (
          <div aria-busy="true" className="h-24 animate-pulse rounded-md bg-muted">
            <span className="sr-only">Loading…</span>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={FileSearch} title="Nothing exported yet" learnMore="exports-and-watermarks">
            Reports, profile sheets and graph images appear here as soon as someone exports them.
          </EmptyState>
        ) : (
          <>
            <div className="relative overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">
                      When
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Who
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      What
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Format
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Rows
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Export ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.exportId} className="border-t">
                      <td className="whitespace-nowrap px-3 py-2">{when(r.createdAt)}</td>
                      <td className="px-3 py-2">
                        <DataText>{r.userName ?? r.userEmail ?? 'Unknown'}</DataText>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-muted-foreground">{EXPORT_TYPE_LABELS[r.type]} · </span>
                        <DataText>{r.what}</DataText>
                      </td>
                      <td className="px-3 py-2 uppercase">{r.format}</td>
                      <td className="px-3 py-2 text-right">
                        <DataNumber>{r.rowCount ?? '—'}</DataNumber>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.exportId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="flex justify-center">
                <Button size="sm" variant="outline" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                  Show older exports
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Trace() {
  const id = useId();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [hit, setHit] = useState<ExportRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trace = async (e: React.FormEvent) => {
    e.preventDefault();
    setHit(null);
    setError(null);
    const exportId = findExportId(text);
    if (!exportId) return setError('Paste an export ID: 26 letters and digits, printed after “Export” in the watermark.');
    setBusy(true);
    try {
      setHit(await api<ExportRecord>(`/admin/exports/${exportId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="exports-trace" className="space-y-3 rounded-lg border bg-background p-4" data-tour="exports-trace">
      <div className="flex items-center gap-1">
        <h2 id="exports-trace" className="text-base font-semibold">
          Trace a file
        </h2>
        <FieldHelp
          label="Trace a file"
          help="Found an exported file somewhere it shouldn't be? Its watermark reads “Exported by … · date · Export <ID>”. Paste the ID or the whole line."
          learnMore="exports-and-watermarks"
        />
      </div>
      <form onSubmit={trace} className="flex flex-wrap items-end gap-2">
        <div className="min-w-64 flex-1 space-y-1">
          <label htmlFor={id} className="text-xs font-medium">
            Export ID or watermark line
          </label>
          <input
            id={id}
            dir="auto"
            className="h-9 w-full rounded-md border border-input bg-surface px-2 font-mono text-sm"
            value={text}
            placeholder="Export 01K…"
            onChange={e => setText(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy || !text.trim()}>
          <Search aria-hidden="true" /> {busy ? 'Looking…' : 'Trace'}
        </Button>
      </form>
      <EditorError error={error} />
      {hit && (
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]" data-testid="export-trace-result">
          <dt className="text-muted-foreground">Exported by</dt>
          <dd>
            <DataText>{[hit.userName, hit.userEmail].filter(Boolean).join(' · ') || 'Unknown'}</DataText>
          </dd>
          <dt className="text-muted-foreground">When</dt>
          <dd>{when(hit.createdAt)}</dd>
          <dt className="text-muted-foreground">What</dt>
          <dd>
            {EXPORT_TYPE_LABELS[hit.type]} · <DataText>{hit.what}</DataText>
          </dd>
          <dt className="text-muted-foreground">Format</dt>
          <dd className="uppercase">{hit.format}</dd>
          <dt className="text-muted-foreground">Rows · size</dt>
          <dd>
            <DataNumber>{hit.rowCount ?? '—'}</DataNumber> · {size(hit.bytes)}
          </dd>
          <dt className="text-muted-foreground">Watermark</dt>
          <dd className="font-mono text-xs">{hit.watermark}</dd>
        </dl>
      )}
    </section>
  );
}

const ROLE_LABELS: Record<(typeof EXPORT_POLICY_ROLES)[number], string> = {
  editor: 'Editors',
  reviewer: 'Reviewers',
  viewer: 'Viewers',
};

function PolicySection() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const q = useQuery({ queryKey: ['admin', 'export-policy'], queryFn: () => api<ExportControlPolicy>('/admin/export-policy') });
  const [draft, setDraft] = useState<ExportControlPolicy | null>(null);
  const [text, setText] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (!q.data) return;
    setDraft(q.data);
    setText(Object.fromEntries(EXPORT_ALERT_RULES.map(r => [r, String(q.data.thresholds[r])])));
  }, [q.data]);
  const save = useMutation({
    mutationFn: (p: ExportControlPolicy) => withReauth(() => api<ExportControlPolicy>('/admin/export-policy', { method: 'PUT', body: p })),
    onSuccess: p => {
      qc.setQueryData(['admin', 'export-policy'], p);
      setMessage({ kind: 'ok', text: 'Export policy saved. It applies to the next export anyone makes.' });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong. Try again.' });
    },
  });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setMessage(null);
    const thresholds = { ...draft.thresholds };
    for (const r of EXPORT_ALERT_RULES) {
      const raw = (text[r] ?? '').trim();
      const v = Number(raw);
      if (!/^\d+$/.test(raw) || v > MAX_EXPORT_THRESHOLD)
        return setMessage({
          kind: 'error',
          text: `“${EXPORT_ALERT_LABELS[r].label}” must be a whole number from 0 (off) to ${MAX_EXPORT_THRESHOLD.toLocaleString()}.`,
        });
      thresholds[r] = v;
    }
    save.mutate({ ...draft, thresholds });
  };
  return (
    <section aria-labelledby="exports-policy" className="space-y-3 rounded-lg border bg-background p-4" data-tour="exports-policy">
      <div className="flex items-center gap-1">
        <h2 id="exports-policy" className="text-base font-semibold">
          Export policy
        </h2>
        <FieldHelp
          label="Export policy"
          help="Exports are always watermarked and registered. Here you choose who may export, and when an export counts as bulk and alerts the admins."
          learnMore="export-controls"
        />
      </div>
      {q.isError ? (
        <ErrorState error={q.error} title="The export policy couldn’t be loaded" onRetry={() => q.refetch()} learnMore="export-controls" />
      ) : !draft ? (
        <div aria-busy="true" className="h-24 animate-pulse rounded-md bg-muted">
          <span className="sr-only">Loading…</span>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Who may export</legend>
            <p className="text-xs text-muted-foreground">Admins can always export. Turning a role off hides its export buttons.</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
              {EXPORT_POLICY_ROLES.map(r => (
                <label key={r} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.roles[r]}
                    onChange={e => setDraft({ ...draft, roles: { ...draft.roles, [r]: e.target.checked } })}
                  />
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Alert admins when</legend>
            <p className="text-xs text-muted-foreground">
              The export is still made. Every admin gets a notification and an email that links here. 0 turns a rule off.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {EXPORT_ALERT_RULES.map(r => (
                <ThresholdInput key={r} rule={r} value={text[r] ?? ''} onChange={v => setText({ ...text, [r]: v })} />
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              <Save aria-hidden="true" /> {save.isPending ? 'Saving…' : 'Save policy'}
            </Button>
            {message && (
              <p
                role={message.kind === 'error' ? 'alert' : 'status'}
                className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
              >
                {message.text}
              </p>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function ThresholdInput({
  rule,
  value,
  onChange,
}: {
  rule: (typeof EXPORT_ALERT_RULES)[number];
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  const { label, help, unit } = EXPORT_ALERT_LABELS[rule];
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-xs font-medium">
          {label}
        </label>
        <FieldHelp label={label} help={help} learnMore="export-controls" />
      </div>
      <div className="flex items-center gap-2">
        <Input id={id} inputMode="numeric" className="w-32" value={value} onChange={e => onChange(e.target.value)} />
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}

function AlertsSection() {
  const list = useInfiniteQuery({
    queryKey: ['admin', 'export-alerts'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ alerts: ExportAlert[]; next: number | null }>(`/admin/export-alerts${pageParam ? `?before=${pageParam}` : ''}`),
    getNextPageParam: last => last.next ?? undefined,
  });
  const rows = list.data?.pages.flatMap(p => p.alerts) ?? [];
  return (
    <section aria-labelledby="exports-alerts" className="space-y-3" data-tour="exports-alerts">
      <h2 id="exports-alerts" className="text-base font-semibold">
        Bulk-export alerts
      </h2>
      {list.isError ? (
        <ErrorState error={list.error} title="The alerts couldn’t be loaded" onRetry={() => list.refetch()} learnMore="export-controls" />
      ) : list.isPending ? (
        <div aria-busy="true" className="h-16 animate-pulse rounded-md bg-muted">
          <span className="sr-only">Loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={BellOff} title="No bulk exports" learnMore="export-controls">
          When an export crosses one of the thresholds above, it is listed here and every admin is notified.
        </EmptyState>
      ) : (
        <>
          <div className="relative overflow-x-auto rounded-md border">
            <table className="w-full text-sm" data-testid="export-alerts">
              <thead className="bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    When
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Who
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Rule
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Observed
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Threshold
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Export ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(a => (
                  <tr key={a.id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2">{when(a.createdAt)}</td>
                    <td className="px-3 py-2">
                      <DataText>{a.userName ?? a.userEmail ?? 'Unknown'}</DataText>
                    </td>
                    <td className="px-3 py-2">{EXPORT_ALERT_LABELS[a.rule].label}</td>
                    <td className="px-3 py-2 text-right">
                      <DataNumber>{a.observed.toLocaleString()}</DataNumber>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <DataNumber>{a.threshold.toLocaleString()}</DataNumber>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{a.exportId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.hasNextPage && (
            <div className="flex justify-center">
              <Button size="sm" variant="outline" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                Show older alerts
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
